import { useLazyQuery } from '@apollo/client';
import { Add } from '@mui/icons-material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Box, Button, CircularProgress } from '@mui/material';
import {
  NotificationTypeEnum,
  SimpleSelect,
  useDialog,
  useNotification,
} from '@prismamedia/one-components';
import {
  Category,
  GetCategoriesQuery_categories_Category,
  GetMainCategoryAiQuery,
  GetMainCategoryAiQueryVariables,
  GetRecipeQuery_recipe_Recipe,
  SuggestTagsQuery,
  SuggestTagsQueryVariables,
} from '__generated__/queries-recipe';
import { GET_MAIN_CATEGORY_AI } from 'apollo/queries/recipeMainCategoryAi.recipe.graphql';
import { SUGGEST_TAGS } from 'apollo/queries/suggestTags.recipe.graphql';
import { AuthorSelect } from 'components/AuthorSelect';
import { CategorySelect } from 'components/CategorySelect';
import { SecondaryCategoriesSelect } from 'components/SecondaryCategoriesSelect';
import { TimeInput } from 'components/TimeInput';
import {
  calorieLevels,
  difficulties,
  estimatedCosts,
} from 'const/selectsValues';
import { Dispatch, FC, SetStateAction, useCallback } from 'react';
import { TagsDialog } from '../TagsDialog';
import { NEW_RECIPE_CATEGORY_ID } from '../utils';
import { useStyles } from './styles';
import { Tags } from './Tags';
import ReactGA from 'react-ga4';

interface InformationsDrawerProps {
  recipe?: GetRecipeQuery_recipe_Recipe;
  setRecipe: Dispatch<SetStateAction<GetRecipeQuery_recipe_Recipe | undefined>>;
}

export const InformationsDrawer: FC<InformationsDrawerProps> = ({
  recipe,
  setRecipe,
}) => {
  const classes = useStyles();
  const { openDialog } = useDialog();

  const mainCategory = recipe?.recipeCategories.find(
    ({ position }) => position === 1,
  )?.category;

  const { pushNotification } = useNotification();

  const [suggestTags, { loading: loadingSuggestTags }] = useLazyQuery<
    SuggestTagsQuery,
    SuggestTagsQueryVariables
  >(SUGGEST_TAGS, {
    fetchPolicy: 'network-only',
    onError: () => {
      pushNotification({
        message: 'Une erreur est survenue lors de la suggestion de tags',
        type: NotificationTypeEnum.error,
      });
    },
    onCompleted: (suggestTagsData) => {
      if (suggestTagsData?.suggestTags?.tagList) {
        setRecipe((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            aiGenerated: JSON.stringify(
              prev.aiGenerated && JSON.parse(prev.aiGenerated).includes('tags')
                ? JSON.parse(prev.aiGenerated)
                : [...JSON.parse(prev.aiGenerated || '[]'), 'tags'],
            ),
            recipeTags: JSON.parse(
              suggestTagsData?.suggestTags?.tagList as any,
            ).tags.map((tag: { id: string; title: string }, index: number) => ({
              id: `NEW_RECIPE_TAG_ID_${index}`,
              tag: {
                id: tag.id,
                title: tag.title,
              },
            })),
          };
        });
      } else {
        pushNotification({
          message: 'Une erreur est survenue lors de la suggestion de tags',
          type: NotificationTypeEnum.error,
        });
      }
    },
  });

  const [
    getMainCategoryAi,
    { loading: loadingGetMainCategoryAi },
  ] = useLazyQuery<GetMainCategoryAiQuery, GetMainCategoryAiQueryVariables>(
    GET_MAIN_CATEGORY_AI,
    {
      fetchPolicy: 'network-only',
      onError: () => {
        pushNotification({
          message: 'Une erreur est survenue lors de la suggestion de tags',
          type: NotificationTypeEnum.error,
        });
      },
      onCompleted: (mainCategoryAiData) => {
        if (mainCategoryAiData?.getMainCategoryAI?.category) {
          handleMainCategoryChange(
            mainCategoryAiData.getMainCategoryAI
              .category as GetCategoriesQuery_categories_Category,
          );
        } else {
          pushNotification({
            message: 'Une erreur est survenue lors de la suggestion de tags',
            type: NotificationTypeEnum.error,
          });
        }
      },
    },
  );

  const handleRecipeChange = useCallback(
    (key: keyof GetRecipeQuery_recipe_Recipe) => (value: any) => {
      setRecipe((prev) =>
        prev
          ? {
              ...prev,
              [key]: value,
            }
          : prev,
      );
    },
    [setRecipe],
  );

  const handleMainCategoryChange = useCallback(
    (category: GetCategoriesQuery_categories_Category | null) => {
      setRecipe((prev) => {
        if (!prev) return prev;
        const prevRecipeCategory = prev.recipeCategories.find(
          (c) => c.position === 1,
        );

        return {
          ...prev,
          recipeCategories: [
            ...prev.recipeCategories.filter((cat) => cat.position !== 1),
            {
              ...(prevRecipeCategory || {
                position: 1,
                id: NEW_RECIPE_CATEGORY_ID,
              }),
              category,
            },
          ],
        };
      });
    },
    [setRecipe],
  );

  const handleSecondaryCategoriesChange = useCallback(
    (categories: Pick<Category, 'title' | 'id'>[]) => {
      setRecipe((prev) => {
        if (!prev) return prev;

        const prevSecondaryCategories = prev.recipeCategories.filter(
          ({ position }) => position !== 1,
        );

        return {
          ...prev,
          recipeCategories: [
            ...prev.recipeCategories.filter((cat) => cat.position === 1),
            ...categories.map((category, index) => ({
              position: index + 2,
              id: NEW_RECIPE_CATEGORY_ID,
              category,
            })),
            ...prevSecondaryCategories.map((recipeCategory) => ({
              ...recipeCategory,
              category: null,
            })),
          ],
        };
      });
    },
    [setRecipe],
  );

  if (!recipe) return null;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Button
          startIcon={<Add />}
          variant="contained"
          size="small"
          color="inherit"
          classes={{ root: classes.addTagButton }}
          data-testid="tag-button"
          onClick={() =>
            openDialog(
              <TagsDialog
                tags={recipe.recipeTags}
                setTags={(recipeTags) =>
                  setRecipe((prev) => (prev ? { ...prev, recipeTags } : prev))
                }
              />,
              {
                fullScreen: true,
              },
            )
          }
        >
          Ajouter un tag
        </Button>
        {loadingSuggestTags ? (
          <CircularProgress size={16} />
        ) : (
          <Box
            sx={{
              cursor: 'pointer',
              gap: 0.5,
              fontSize: '14px',
            }}
            display={'flex'}
            onClick={() => {
              ReactGA.event({
                category: 'Recipe IA Tag',
                action: 'Recipe IA Generation',
              });
              suggestTags({
                variables: {
                  recipe: JSON.stringify(recipe),
                },
              });
            }}
          >
            <AutoFixHighIcon
              sx={{
                color: '#E91E63',
                fontSize: '18px',
              }}
            />
            <Box
              sx={{
                background: 'linear-gradient(90deg,#E91E63,#C2185B);',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Suggérer
            </Box>
          </Box>
        )}
      </Box>
      <Tags
        recipeTags={recipe.recipeTags}
        setRecipeTags={(recipeTags) =>
          setRecipe((prev) => (prev ? { ...prev, recipeTags } : prev))
        }
      />
      <form className={classes.form} noValidate autoComplete="off">
        <AuthorSelect
          label="Auteur"
          authorId={recipe.author}
          onSelect={handleRecipeChange('author')}
          className={classes.input}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <CategorySelect
              label="Catégorie principale"
              category={mainCategory}
              onSelect={handleMainCategoryChange}
              className={classes.input}
              required
              brandKey={recipe.brandKey}
              selectedCategories={recipe.recipeCategories}
            />
          </Box>
          {loadingGetMainCategoryAi ? (
            <CircularProgress size={16} />
          ) : (
            <AutoFixHighIcon
              onClick={() => {
                setRecipe((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    aiGenerated: JSON.stringify(
                      prev.aiGenerated &&
                        JSON.parse(prev.aiGenerated).includes('mainCategory')
                        ? JSON.parse(prev.aiGenerated)
                        : [
                            ...JSON.parse(prev.aiGenerated || '[]'),
                            'mainCategory',
                          ],
                    ),
                  };
                });
                ReactGA.event({
                  category: 'Recipe IA Main Category',
                  action: 'Recipe IA Generation',
                });
                getMainCategoryAi({
                  variables: {
                    recipe: JSON.stringify(recipe),
                    brandKey: recipe.brandKey as any,
                  },
                });
              }}
              sx={{
                color: '#E91E63',
                cursor: 'pointer',
                fontSize: '21px',
                mb: 1.5,
              }}
            />
          )}
        </Box>
        <SecondaryCategoriesSelect
          label="Catégories secondaires"
          categories={recipe.recipeCategories}
          onChange={handleSecondaryCategoriesChange}
          className={classes.input}
          brandKey={recipe.brandKey}
        />
        <SimpleSelect
          label="Difficulté"
          variant="standard"
          value={recipe.difficulty || ''}
          options={difficulties}
          className={classes.input}
          onChange={(_, value) => handleRecipeChange('difficulty')(value)}
        />
        <SimpleSelect
          label="Niveau de prix"
          variant="standard"
          value={recipe.estimatedCost || ''}
          options={estimatedCosts}
          className={classes.input}
          onChange={(_, value) => handleRecipeChange('estimatedCost')(value)}
        />
        <SimpleSelect
          label="Taux de calories"
          variant="standard"
          value={recipe.calorieLevel || ''}
          options={calorieLevels}
          className={classes.input}
          onChange={(_, value) => handleRecipeChange('calorieLevel')(value)}
        />
        <TimeInput
          label="Temps de préparation"
          value={recipe.prepTime || 0}
          className={classes.input}
          onChange={handleRecipeChange('prepTime')}
        />
        <TimeInput
          label="Temps de cuisson"
          value={recipe.cookTime || 0}
          className={classes.input}
          onChange={handleRecipeChange('cookTime')}
        />
        <TimeInput
          label="Temps d'attente"
          value={recipe.waitTime || 0}
          className={classes.input}
          onChange={handleRecipeChange('waitTime')}
        />
      </form>
    </>
  );
};
